// src/components/StrategyBuilder/ConditionCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Collapse,
  Grid,
  Paper,
  Divider,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FlashOn as FlashIcon,
  CompareArrows as SequenceIcon,
  Block as InterferenceIcon,
  CheckCircle as CheckIcon,
  WarningAmber as WarningIcon,
  TrendingUp as LongIcon,
  TrendingDown as ShortIcon,
} from '@mui/icons-material';

const ConditionCard = ({ condition, index, availableSignals, onUpdate, onDelete }) => {
  const getSignalKey = (signal) => {
    if (!signal) return '';
    return `${signal.algo}:${signal.direction}`;
  };

  const findSignalByKey = (key) => {
    if (!key) return null;
    return availableSignals.find(s => getSignalKey(s) === key);
  };

  const isConfigured = Boolean(condition.type && (
    condition.signals?.length > 0 ||
    condition.trigger ||
    condition.target
  ));
  
  const [expanded, setExpanded] = useState(!isConfigured || isConfigured);

  useEffect(() => {
    if (!condition.timing) {
      onUpdate({
        ...condition,
        timing: { min_minutes: 0, min_seconds: 0, max_minutes: 0, max_seconds: 0 }
      });
    }
  }, []);

  const conditionTypes = [
    {
      value: 'time_bound',  // Changed from 'sequential' to match backend
      label: 'Time Bound',
      icon: SequenceIcon,
      color: '#8b5cf6',
      bg: '#f5f3ff',
      description: 'Target must fire within time window after trigger',
    },
    {
      value: 'simultaneous',
      label: 'Simultaneous',
      icon: FlashIcon,
      color: '#3b82f6',
      bg: '#eff6ff',
      description: 'All signals must occur at the same time',
    },
    {
      value: 'interference',
      label: 'Interference',
      icon: InterferenceIcon,
      color: '#ef4444',
      bg: '#fef2f2',
      description: 'Target fires after trigger ends',
    },
  ];

  const selectedType = conditionTypes.find(t => t.value === condition.type);

  const handleChange = (field, value) => {
    onUpdate({ ...condition, [field]: value });
  };

  const handleTimingChange = (field, value) => {
    onUpdate({
      ...condition,
      timing: { 
        ...(condition.timing || { min_minutes: 0, min_seconds: 0, max_minutes: 0, max_seconds: 0 }),
        [field]: parseInt(value) || 0 
      }
    });
  };

  const handleTypeChange = (newType) => {
    const updates = {
      ...condition,
      type: newType,
    };

    if (newType === 'simultaneous') {
      // For simultaneous, use array of signal keys
      updates.trigger = null;
      updates.target = null;
      updates.interference_signals = [];
      updates.timing = { min_minutes: 0, min_seconds: 0, max_minutes: 0, max_seconds: 0 };
      if (!updates.signals) {
        updates.signals = [];
      }
    } else if (newType === 'time_bound' || newType === 'interference') {
      // For time_bound and interference, use signal objects
      updates.signals = [];
      if (!updates.timing) {
        updates.timing = { min_minutes: 0, min_seconds: 0, max_minutes: 0, max_seconds: 0 };
      }
    }

    onUpdate(updates);
  };

  const handleSignalSelection = (field, selectedKey) => {
    const signal = findSignalByKey(selectedKey);
    if (signal) {
      onUpdate({
        ...condition,
        [field]: {
          algo: signal.algo,
          direction: signal.direction
        }
      });
    }
  };

  const handleMultipleSignalSelection = (selectedKeys) => {
    const signals = selectedKeys.map(key => findSignalByKey(key)).filter(Boolean);
    onUpdate({
      ...condition,
      signals: signals.map(s => ({
        algo: s.algo,
        direction: s.direction
      }))
    });
  };

  const getConditionSummary = () => {
    if (!condition.type) return 'Click to configure this condition';

    if (condition.type === 'simultaneous') {
      const count = condition.signals?.length || 0;
      if (count === 0) return 'No signals selected';
      return `${count} signal${count !== 1 ? 's' : ''} must occur simultaneously`;
    }

    if (condition.type === 'time_bound' || condition.type === 'interference') {
      if (condition.trigger && condition.target) {
        const triggerKey = getSignalKey(condition.trigger);
        const targetKey = getSignalKey(condition.target);
        const summary = `${triggerKey} → ${targetKey}`;
        if (condition.type === 'interference' && condition.interference_signals?.length > 0) {
          return `${summary} (${condition.interference_signals.length} blocker${condition.interference_signals.length !== 1 ? 's' : ''})`;
        }
        return summary;
      }
      return 'Signals not fully configured';
    }

    return 'Configured';
  };

  const isFullyConfigured = () => {
    if (!condition.type) return false;
    
    if (condition.type === 'simultaneous') {
      return condition.signals && condition.signals.length >= 2;
    }
    
    if (condition.type === 'time_bound' || condition.type === 'interference') {
      return condition.trigger && condition.target;
    }
    
    return false;
  };

  const fullyConfigured = isFullyConfigured();

  const SignalChip = ({ signal }) => {
    if (!signal) return null;
    const isLong = signal.direction === 'LONG';
    const DirectionIcon = isLong ? LongIcon : ShortIcon;
    const directionColor = isLong ? '#10b981' : '#ef4444';
    
    return (
      <Chip
        icon={<DirectionIcon sx={{ fontSize: '14px !important' }} />}
        label={getSignalKey(signal)}
        size="small"
        sx={{
          bgcolor: isLong ? '#d1fae5' : '#fee2e2',
          color: directionColor,
          fontWeight: 600,
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          '& .MuiChip-icon': {
            color: directionColor,
          },
        }}
      />
    );
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '2px solid',
        borderColor: expanded 
          ? (selectedType?.color || '#e2e8f0') 
          : (fullyConfigured ? '#10b981' : '#f59e0b'),
        boxShadow: expanded
          ? `0 8px 24px ${alpha(selectedType?.color || '#000', 0.12)}`
          : fullyConfigured
          ? '0 4px 12px rgba(16, 185, 129, 0.15)'
          : '0 4px 12px rgba(245, 158, 11, 0.15)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'visible',
        '&:hover': {
          boxShadow: `0 12px 32px ${alpha(selectedType?.color || '#000', 0.16)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: selectedType
            ? `linear-gradient(135deg, ${selectedType.bg} 0%, ${alpha(selectedType.color, 0.05)} 100%)`
            : fullyConfigured
            ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
            : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderBottom: '1px solid',
          borderColor: alpha(selectedType?.color || (fullyConfigured ? '#10b981' : '#f59e0b'), 0.2),
          p: 2.5,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: selectedType 
              ? alpha(selectedType.color, 0.08) 
              : fullyConfigured
              ? '#d1fae5'
              : '#fef3c7',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: 'white',
              border: '2px solid',
              borderColor: selectedType?.color || (fullyConfigured ? '#10b981' : '#f59e0b'),
              boxShadow: `0 2px 8px ${alpha(selectedType?.color || (fullyConfigured ? '#10b981' : '#f59e0b'), 0.12)}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 800,
                color: selectedType?.color || (fullyConfigured ? '#10b981' : '#f59e0b'),
              }}
            >
              {index + 1}
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '1.125rem',
                }}
              >
                {condition.name || `Condition ${index + 1}`}
              </Typography>
              {selectedType && (
                <Chip
                  icon={<selectedType.icon sx={{ fontSize: '16px !important' }} />}
                  label={selectedType.label}
                  size="small"
                  sx={{
                    bgcolor: selectedType.bg,
                    color: selectedType.color,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-icon': {
                      color: selectedType.color,
                    },
                  }}
                />
              )}
              {fullyConfigured ? (
                <Chip
                  icon={<CheckIcon sx={{ fontSize: '14px !important' }} />}
                  label="Configured"
                  size="small"
                  sx={{
                    bgcolor: '#d1fae5',
                    color: '#047857',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                    '& .MuiChip-icon': {
                      color: '#047857',
                    },
                  }}
                />
              ) : (
                <Chip
                  icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                  label="Not configured"
                  size="small"
                  sx={{
                    bgcolor: '#fef3c7',
                    color: '#d97706',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                    '& .MuiChip-icon': {
                      color: '#d97706',
                    },
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
              {expanded ? (selectedType?.description || 'Select a condition type to get started') : getConditionSummary()}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Delete Condition" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                sx={{
                  bgcolor: 'white',
                  border: '2px solid #fee2e2',
                  color: '#ef4444',
                  '&:hover': {
                    bgcolor: '#fef2f2',
                    borderColor: '#fecaca',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              sx={{
                bgcolor: 'white',
                border: '2px solid #e2e8f0',
                color: '#64748b',
                '&:hover': {
                  bgcolor: '#f8fafc',
                  borderColor: '#cbd5e1',
                },
                transition: 'all 0.2s',
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Condition Name */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#475569',
                  mb: 1,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Condition Name
              </Typography>
              <TextField
                fullWidth
                value={condition.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Bullish Reversal Pattern"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: selectedType?.color || '#667eea',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontWeight: 600,
                    fontSize: '1rem',
                  },
                }}
              />
            </Box>

            <Divider />

            {/* Condition Type Selection */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#475569',
                  mb: 2,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Condition Type *
              </Typography>
              <Grid container spacing={2}>
                {conditionTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = condition.type === type.value;
                  return (
                    <Grid item xs={12} sm={4} key={type.value}>
                      <Paper
                        onClick={() => handleTypeChange(type.value)}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: isSelected ? type.color : '#e2e8f0',
                          bgcolor: isSelected ? type.bg : 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: type.color,
                            bgcolor: alpha(type.color, 0.05),
                            transform: 'translateY(-4px)',
                            boxShadow: `0 8px 16px ${alpha(type.color, 0.15)}`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            bgcolor: isSelected ? 'white' : type.bg,
                            border: isSelected ? `2px solid ${type.color}` : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                            transition: 'all 0.2s',
                          }}
                        >
                          <Icon sx={{ color: type.color, fontSize: 28 }} />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: '#1e293b',
                            mb: 0.5,
                          }}
                        >
                          {type.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            display: 'block',
                            lineHeight: 1.4,
                          }}
                        >
                          {type.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Conditional Fields Based on Type */}
            {condition.type && (
              <>
                <Divider />

                {/* Simultaneous Signals */}
                {condition.type === 'simultaneous' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: '#475569',
                        mb: 1.5,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Required Signals (All Must Occur) *
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        multiple
                        value={(condition.signals || []).map(getSignalKey)}
                        onChange={(e) => handleMultipleSignalSelection(e.target.value)}
                        renderValue={(selected) => (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {selected.length === 0 ? (
                              <Typography sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                Select at least 2 signals...
                              </Typography>
                            ) : (
                              selected.map((key) => {
                                const signal = findSignalByKey(key);
                                return signal ? <SignalChip key={key} signal={signal} /> : null;
                              })
                            )}
                          </Stack>
                        )}
                        displayEmpty
                        sx={{
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0',
                            borderWidth: 2,
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#cbd5e1',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                            borderWidth: 2,
                          },
                        }}
                      >
                        {availableSignals.map((signal) => {
                          const key = getSignalKey(signal);
                          return (
                            <MenuItem key={key} value={key}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: (condition.signals || []).some(s => getSignalKey(s) === key) ? '#3b82f6' : '#cbd5e1',
                                  }}
                                />
                                <SignalChip signal={signal} />
                              </Stack>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Time Bound/Interference Fields */}
                {(condition.type === 'time_bound' || condition.type === 'interference') && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: '#475569',
                            mb: 1.5,
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Trigger Signal *
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={condition.trigger ? getSignalKey(condition.trigger) : ''}
                            onChange={(e) => handleSignalSelection('trigger', e.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) return <em>Select trigger signal</em>;
                              const signal = findSignalByKey(selected);
                              return signal ? <SignalChip signal={signal} /> : <em>Select trigger signal</em>;
                            }}
                            sx={{
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e2e8f0',
                                borderWidth: 2,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#cbd5e1',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: selectedType?.color || '#667eea',
                                borderWidth: 2,
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select trigger signal</em>
                            </MenuItem>
                            {availableSignals.map((signal) => {
                              const key = getSignalKey(signal);
                              return (
                                <MenuItem key={key} value={key}>
                                  <SignalChip signal={signal} />
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: '#475569',
                            mb: 1.5,
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Target Signal *
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={condition.target ? getSignalKey(condition.target) : ''}
                            onChange={(e) => handleSignalSelection('target', e.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) return <em>Select target signal</em>;
                              const signal = findSignalByKey(selected);
                              return signal ? <SignalChip signal={signal} /> : <em>Select target signal</em>;
                            }}
                            sx={{
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e2e8f0',
                                borderWidth: 2,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#cbd5e1',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: selectedType?.color || '#667eea',
                                borderWidth: 2,
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select target signal</em>
                            </MenuItem>
                            {availableSignals.map((signal) => {
                              const key = getSignalKey(signal);
                              return (
                                <MenuItem key={key} value={key}>
                                  <SignalChip signal={signal} />
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Timing Window */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: '#475569',
                          mb: 1.5,
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Time Window (Between Signals)
                      </Typography>
                      <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block' }}>
                              MINIMUM TIME
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <TextField
                                type="number"
                                label="Minutes"
                                value={condition.timing?.min_minutes || 0}
                                onChange={(e) => handleTimingChange('min_minutes', e.target.value)}
                                inputProps={{ min: 0 }}
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                              <TextField
                                type="number"
                                label="Seconds"
                                value={condition.timing?.min_seconds || 0}
                                onChange={(e) => handleTimingChange('min_seconds', e.target.value)}
                                inputProps={{ min: 0, max: 59 }}
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            </Stack>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block' }}>
                              MAXIMUM TIME
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <TextField
                                type="number"
                                label="Minutes"
                                value={condition.timing?.max_minutes || 0}
                                onChange={(e) => handleTimingChange('max_minutes', e.target.value)}
                                inputProps={{ min: 0 }}
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                              <TextField
                                type="number"
                                label="Seconds"
                                value={condition.timing?.max_seconds || 0}
                                onChange={(e) => handleTimingChange('max_seconds', e.target.value)}
                                inputProps={{ min: 0, max: 59 }}
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            </Stack>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  </>
                )}
              </>
            )}
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ConditionCard;

